import type { SRDSpell } from "../types/srd.js";
import type {
  Spell,
  SpellTradition,
  SpellTargeting,
  SpellAttackType,
  SpellTargetTeam,
  SpellUpcastBlock,
} from "../types/spells.js";
import type { DamageType } from "../types/combat.js";
import type { AbilityName, ConditionId } from "../types/entity.js";
import { getConditionDef } from "../types/condition.js";
import { dataRegistry } from "./DataRegistry.js";

const DAMAGE_TYPES = new Set<string>([
  "acid", "bludgeoning", "cold", "fire", "force", "lightning",
  "necrotic", "piercing", "poison", "psychic", "radiant", "slashing", "thunder",
]);

function asDamageType(raw: string): DamageType {
  if (DAMAGE_TYPES.has(raw)) return raw as DamageType;
  throw new Error(`Unknown damage type: "${raw}"`);
}

const ABILITY_NAMES = new Set<string>(["STR", "DEX", "CON", "INT", "WIS", "CHA"]);

function asAbilityName(raw: string): AbilityName {
  if (ABILITY_NAMES.has(raw)) return raw as AbilityName;
  throw new Error(`Unknown ability name: "${raw}"`);
}

/**
 * Parse SRD `range` string into grid cells.
 *   "Self"      → 0
 *   "Touch"     → 1   (adjacent — strict 5e is 0 but the picker treats it as 1)
 *   "<N> feet"  → floor(N / 5)
 * Throws on anything else so authors get a loud error rather than silent
 * fallback to a wrong number.
 */
export function parseRangeCells(raw: string): number {
  const s = raw.trim();
  if (s === "Self") return 0;
  if (s === "Touch") return 1;
  const m = /^(\d+)\s*feet$/i.exec(s);
  if (m && m[1] !== undefined) return Math.floor(Number(m[1]) / 5);
  throw new Error(`Unparseable spell range: "${raw}"`);
}

function inferTargetTeam(
  raw: SRDSpell,
  targeting: SpellTargeting,
  attackType: SpellAttackType,
): SpellTargetTeam {
  if (raw.target_team) return raw.target_team;
  // aoe/self ignore team — pick a sensible default anyway.
  if (targeting === "self") return "ally";
  if (targeting === "aoe") return "enemy";
  // Heals and pure buffs (no damage block, no save) read as ally; everything
  // else is enemy. Authors override with `target_team` when this guess wrong.
  if (raw.heal_dice !== undefined) return "ally";
  if (attackType === "none" && raw.damage_dice === undefined) return "ally";
  return "enemy";
}

/**
 * Convert a raw SRDSpell JSON record into the runtime Spell type.
 * Throws if required bkcrawl combat fields are malformed.
 */
export function resolveSpell(raw: SRDSpell): Spell {
  const tradition: SpellTradition = raw.tradition ?? "arcane";
  const targeting: SpellTargeting = raw.targeting ?? "single";
  const attackType: SpellAttackType = raw.attack_type ?? "none";
  const targetTeam = inferTargetTeam(raw, targeting, attackType);
  const rangeCells = parseRangeCells(raw.range);

  let upcast: SpellUpcastBlock | undefined;
  if (raw.upcast) {
    upcast = {
      ...(raw.upcast.damage_dice_per_slot !== undefined && { damageDicePerSlot: raw.upcast.damage_dice_per_slot }),
      ...(raw.upcast.heal_dice_per_slot !== undefined && { healDicePerSlot: raw.upcast.heal_dice_per_slot }),
      ...(raw.upcast.extra_count_per_slot !== undefined && { extraCountPerSlot: raw.upcast.extra_count_per_slot }),
      ...(raw.upcast.hp_bonus_per_slot !== undefined && { hpBonusPerSlot: raw.upcast.hp_bonus_per_slot }),
    };
  }

  return {
    id: raw.index,
    name: raw.name,
    level: raw.level,
    school: raw.school.name,
    tradition,
    targeting,
    attackType,
    targetTeam,
    rangeCells,
    ...(raw.min_targets !== undefined && { minTargets: raw.min_targets }),
    ...(raw.max_targets !== undefined && { maxTargets: raw.max_targets }),
    includeDead: raw.include_dead ?? false,
    ...(raw.save_ability !== undefined && { saveAbility: asAbilityName(raw.save_ability) }),
    halfOnSave: raw.half_on_save ?? false,
    ...(raw.damage_dice !== undefined && raw.damage_type !== undefined && {
      damage: { dice: raw.damage_dice, type: asDamageType(raw.damage_type) },
    }),
    ...(raw.heal_dice !== undefined && { heal: { dice: raw.heal_dice } }),
    ...resolveAppliesCondition(raw),
    isConcentration: raw.is_concentration ?? false,
    isReaction: raw.is_reaction ?? false,
    ...(upcast !== undefined && { upcast }),
    desc: Array.isArray(raw.desc) ? raw.desc.join(" ") : "",
  };
}

/**
 * Fetch a spell from the DataRegistry and resolve it to the runtime type.
 * Returns null if the spell JSON does not exist.
 */
export async function loadSpell(id: string): Promise<Spell | null> {
  const raw = await dataRegistry.getSpell(id);
  if (!raw) return null;
  return resolveSpell(raw);
}

/**
 * EV-E5 — map `applies_condition` onto the runtime spell, validating the
 * condition id against the registry. Unknown ids warn and are dropped so
 * a typo degrades to "no condition" rather than a broken cast.
 */
function resolveAppliesCondition(
  raw: SRDSpell,
): { appliesCondition: NonNullable<Spell["appliesCondition"]> } | Record<string, never> {
  const ac = raw.applies_condition;
  if (!ac) return {};
  if (!getConditionDef(ac.id as ConditionId)) {
    console.warn(`[SpellLoader] ${raw.index}: unknown applies_condition id "${ac.id}" — dropped`);
    return {};
  }
  return {
    appliesCondition: {
      id: ac.id as ConditionId,
      ...(ac.duration_s !== undefined ? { durationSec: ac.duration_s } : {}),
    },
  };
}
